import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../App';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import { motion } from 'framer-motion';
import { Check, Star, Shield, Zap, Users, Trophy, BookOpen, ArrowRight, Calculator, FlaskConical, Globe, Clock } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEN = language === 'en';

  const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-50px" }, transition: { duration: 0.5 } };

  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Nav */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-logo">
            <img src="/pantass-logo.jpg" alt="PANTASS" style={{ width: 32, height: 32, borderRadius: 8 }} />
            <span>PANTASS</span>
          </div>

          {/* Desktop nav */}
          <div className="landing-nav-links">
            <button onClick={() => scrollTo('pricing-section')}>{isEN ? 'Pricing' : 'Harga'}</button>
            <a href="/blog/tuisyen-vs-app-belajar">Blog</a>
            <button onClick={() => scrollTo('affiliate-section')}>{isEN ? 'Earn Money' : 'Jana Wang'}</button>
            <button onClick={() => scrollTo('testimonials-section')}>{isEN ? 'Testimonials' : 'Testimoni'}</button>
          </div>

          <div className="landing-nav-right">
            <LanguageToggle />
            <button className="landing-nav-login" onClick={() => navigate('/login')}>
              {isEN ? 'Log In' : 'Log Masuk'}
            </button>
            <button className="landing-nav-cta" onClick={() => navigate('/register')}>
              {isEN ? 'Start Free' : 'Mula Percuma'}
            </button>
          </div>

          {/* Mobile: Language toggle + Hamburger */}
          <div className="landing-mobile-right">
            <LanguageToggle />
            <button className="landing-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                {menuOpen ? <><line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" /></> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="landing-mobile-menu">
            <button onClick={() => scrollTo('pricing-section')}>{isEN ? 'Pricing' : 'Harga'}</button>
            <a href="/blog/tuisyen-vs-app-belajar" onClick={() => setMenuOpen(false)} style={{ textDecoration: 'none', color: 'inherit', display: 'block', padding: '12px 0' }}>Blog</a>
            <button onClick={() => scrollTo('affiliate-section')}>{isEN ? 'Earn Money' : 'Jana Wang'}</button>
            <button onClick={() => scrollTo('testimonials-section')}>{isEN ? 'Testimonials' : 'Testimoni'}</button>
            <button onClick={() => { setMenuOpen(false); navigate('/login'); }}>{isEN ? 'Log In' : 'Log Masuk'}</button>
            <button className="landing-mobile-cta" onClick={() => { setMenuOpen(false); navigate('/register'); }}>{isEN ? 'Start Free' : 'Mula Percuma'}</button>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="landing-hero">
        <motion.div className="landing-hero-content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="landing-hero-badge">{isEN ? 'Trusted by 500+ Malaysian Schools' : 'Dipercayai 500+ Sekolah Malaysia'}</div>
          <h1 className="landing-hero-title">
            {isEN ? (
              <>Is Your Child <span className="landing-highlight">Afraid of Math?</span><br/>Turn Learning Into a <span className="landing-highlight-2">Game.</span></>
            ) : (
              <>Anak Anda <span className="landing-highlight">Takut Matematik?</span><br/>Jadikan Belajar Satu <span className="landing-highlight-2">Permainan.</span></>
            )}
          </h1>
          <p className="landing-hero-desc">
            {isEN
              ? 'PANTASS turns boring homework into an exciting tug-of-war battle. Watch your child\'s grades improve while they think they\'re just playing a game.'
              : 'PANTASS ubah kerja rumah yang membosankan jadi pertandingan tarik tali yang seru. Lihat markah anak anda meningkat sambil mereka rasa mereka cuma bermain.'}
          </p>
          <div className="landing-hero-actions">
            <a href="/register" className="landing-cta-primary" data-testid="hero-cta-register">
              {isEN ? 'Start Free — No Card Needed' : 'Mula Percuma — Tanpa Kad'}
              <ArrowRight size={20} />
            </a>
            <div className="landing-hero-trust">
              <div className="landing-stars">
                {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="#eab308" color="#eab308" />)}
              </div>
              <span>{isEN ? '4.9/5 from 2,300+ parents' : '4.9/5 daripada 2,300+ ibu bapa'}</span>
            </div>
          </div>
        </motion.div>
        <motion.div className="landing-hero-visual" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}>
          <div className="landing-game-preview">
            <div className="landing-preview-team landing-preview-team1">
              <div className="landing-preview-badge">{isEN ? 'Team 1' : 'Pasukan 1'}</div>
              <div className="landing-preview-q">7 + 4 = ?</div>
              <div className="landing-preview-ans">11</div>
            </div>
            <div className="landing-preview-center">
              <svg viewBox="0 0 200 80" width="200" height="80">
                <line x1="20" y1="40" x2="180" y2="40" stroke="#b8956a" strokeWidth="6" strokeLinecap="round" />
                <polygon points="95,20 95,42 110,31" fill="#ef4444" />
                <circle cx="60" cy="38" r="12" fill="#1a8cff" /><circle cx="140" cy="38" r="12" fill="#ff8c1a" />
              </svg>
            </div>
            <div className="landing-preview-team landing-preview-team2">
              <div className="landing-preview-badge">{isEN ? 'Team 2' : 'Pasukan 2'}</div>
              <div className="landing-preview-q">9 + 6 = ?</div>
              <div className="landing-preview-ans">15</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* PAIN POINTS */}
      <section className="landing-pain">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <h2 className="landing-pain-title">
            {isEN ? 'Does This Sound Like Your Child?' : 'Adakah Ini Macam Anak Anda?'}
          </h2>
          <div className="landing-pain-grid">
            {[
              { emoji: '😫', text: isEN ? '"I hate math! It\'s so boring!"' : '"Saya benci matematik! Boring sangat!"' },
              { emoji: '📱', text: isEN ? 'Spends hours on phone but won\'t touch homework' : 'Berjam-jam pegang telefon tapi tak sentuh kerja rumah' },
              { emoji: '😰', text: isEN ? 'Grades dropping every semester' : 'Markah merosot setiap semester' },
              { emoji: '💸', text: isEN ? 'Tuition fees keep rising but no improvement' : 'Yuran tuisyen makin naik tapi tiada peningkatan' },
            ].map((pain, i) => (
              <div key={i} className="landing-pain-card">
                <span className="landing-pain-emoji">{pain.emoji}</span>
                <p>{pain.text}</p>
              </div>
            ))}
          </div>
          <p className="landing-pain-solution">
            {isEN
              ? 'You\'re not alone. 73% of Malaysian parents face the same struggle. But what if learning felt like playing a game?'
              : 'Anda tidak keseorangan. 73% ibu bapa Malaysia hadapi masalah yang sama. Tapi bagaimana kalau belajar terasa macam bermain?'}
          </p>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="landing-how">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <h2 className="landing-section-title">{isEN ? 'How PANTASS Works' : 'Bagaimana PANTASS Berfungsi'}</h2>
          <div className="landing-steps">
            {[
              { num: '1', icon: BookOpen, title: isEN ? 'Choose a Subject' : 'Pilih Subjek', desc: isEN ? '8 subjects from Math to Science, BM to English' : '8 subjek dari Matematik ke Sains, BM ke English' },
              { num: '2', icon: Users, title: isEN ? 'Challenge a Friend' : 'Cabaran Kawan', desc: isEN ? 'Two players compete on the same screen — who\'s faster?' : 'Dua pemain berlawan pada skrin yang sama — siapa lagi pantas?' },
              { num: '3', icon: Trophy, title: isEN ? 'Win & Learn!' : 'Menang & Belajar!', desc: isEN ? 'Answer correctly to pull the rope. First to win gets the trophy!' : 'Jawab betul untuk tarik tali. Yang pertama menang dapat trofi!' },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="landing-step">
                  <div className="landing-step-num">{step.num}</div>
                  <Icon size={28} className="landing-step-icon" />
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* TESTIMONIALS */}
      <section className="landing-testimonials" id="testimonials-section">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <h2 className="landing-section-title">{isEN ? 'What Parents Say' : 'Apa Kata Ibu Bapa'}</h2>
          <div className="landing-testimonial-grid">
            {[
              { name: 'Puan Noraini', role: isEN ? 'Mother of 3, Selangor' : 'Ibu kepada 3, Selangor', text: isEN ? '"My son used to cry doing math homework. Now he begs me to play PANTASS every day. His marks went from C to A in 2 months!"' : '"Anak saya dulu menangis buat kerja rumah matematik. Sekarang dia merayu nak main PANTASS setiap hari. Markah dia naik dari C ke A dalam 2 bulan!"', stars: 5 },
              { name: 'Cikgu Hafiz', role: isEN ? 'Teacher, SK Bukit Jalil' : 'Guru, SK Bukit Jalil', text: isEN ? '"I use PANTASS in my class every Friday. The students are so excited, they actually look forward to math class now. Best RM199 my school ever spent."' : '"Saya guna PANTASS dalam kelas setiap Jumaat. Murid-murid sangat teruja, mereka nantikan kelas matematik sekarang. Terbaik RM199 sekolah pernah belanja."', stars: 5 },
              { name: 'Encik Razak', role: isEN ? 'Father, Johor Bahru' : 'Bapa, Johor Bahru', text: isEN ? '"Cheaper than tuition, more effective than workbooks. My daughter improved 30 marks in Science after using PANTASS for just 1 month."' : '"Lebih murah dari tuisyen, lebih berkesan dari buku latihan. Anak perempuan saya naik 30 markah Sains selepas guna PANTASS 1 bulan sahaja."', stars: 5 },
            ].map((testi, i) => (
              <div key={i} className="landing-testimonial-card">
                <div className="landing-testimonial-stars">{Array(testi.stars).fill(0).map((_, j) => <Star key={j} size={14} fill="#eab308" color="#eab308" />)}</div>
                <p className="landing-testimonial-text">{testi.text}</p>
                <div className="landing-testimonial-author">
                  <strong>{testi.name}</strong>
                  <span>{testi.role}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* STATS */}
      <section className="landing-stats">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <div className="landing-stats-grid">
            {[
              { num: '8', label: isEN ? 'Subjects' : 'Subjek' },
              { num: '340+', label: isEN ? 'Questions' : 'Soalan' },
              { num: '2,300+', label: isEN ? 'Happy Parents' : 'Ibu Bapa Gembira' },
              { num: '500+', label: isEN ? 'Schools' : 'Sekolah' },
            ].map((stat, i) => (
              <div key={i} className="landing-stat-item">
                <div className="landing-stat-num">{stat.num}</div>
                <div className="landing-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section className="landing-features">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <h2 className="landing-section-title">{isEN ? 'Why Parents Love PANTASS' : 'Kenapa Ibu Bapa Suka PANTASS'}</h2>
          <div className="landing-features-grid">
            {[
              { icon: Zap, title: isEN ? 'Makes Learning Fun' : 'Jadikan Belajar Seronok', desc: isEN ? 'Competitive tug-of-war format with sound effects, streaks, and celebrations' : 'Format tarik tali kompetitif dengan kesan bunyi, kombo, dan perayaan' },
              { icon: Shield, title: isEN ? 'Safe for Kids' : 'Selamat untuk Kanak-Kanak', desc: isEN ? 'No ads, no in-app purchases, no inappropriate content. COPPA compliant.' : 'Tiada iklan, tiada pembelian dalam app, tiada kandungan tidak sesuai.' },
              { icon: BookOpen, title: isEN ? 'Follows Malaysian Syllabus' : 'Ikut Silibus Malaysia', desc: isEN ? 'Questions aligned with KSSR (Primary) and KSSM (Secondary) curriculum' : 'Soalan selaras dengan kurikulum KSSR (Rendah) dan KSSM (Menengah)' },
              { icon: Users, title: isEN ? 'Teacher Dashboard' : 'Dashboard Guru', desc: isEN ? 'Track each student\'s progress, create classes, export PDF reports' : 'Jejak kemajuan setiap pelajar, buat kelas, eksport laporan PDF' },
              { icon: Trophy, title: isEN ? 'School Tournaments' : 'Pertandingan Sekolah', desc: isEN ? 'Bracket-style competitions between classes. Perfect for school events!' : 'Pertandingan bracket antara kelas. Sesuai untuk acara sekolah!' },
              { icon: Star, title: isEN ? 'Achievements & Streaks' : 'Pencapaian & Kombo', desc: isEN ? '8 badges to unlock, combo bonuses, and WhatsApp sharing to show off!' : '8 lencana untuk buka kunci, bonus kombo, dan kongsi ke WhatsApp!' },
            ].map((feat, i) => {
              const Icon = feat.icon;
              return (
                <div key={i} className="landing-feature-card">
                  <div className="landing-feature-icon"><Icon size={24} /></div>
                  <h3>{feat.title}</h3>
                  <p>{feat.desc}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* PRICING SECTION */}
      <section className="landing-pricing" id="pricing-section">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <h2 className="landing-section-title">{isEN ? 'Simple, Affordable Pricing' : 'Harga Mudah & Berpatutan'}</h2>
          <p style={{ textAlign: 'center', color: '#666', marginTop: '-20px', marginBottom: '32px', fontSize: '16px' }}>{isEN ? 'Cheaper than 1 hour of tuition. More fun than 1 hour of screen time.' : 'Lebih murah dari 1 jam tuisyen. Lebih seronok dari 1 jam screen time.'}</p>
          <div className="landing-pricing-grid">
            {[
              { name: 'Free', nameBM: 'Percuma', price: 'RM0', color: '#6b7280', features: isEN ? ['25 games/month', '2 subjects', 'Easy difficulty', 'World Leaderboard'] : ['25 permainan/bulan', '2 subjek', 'Tahap Mudah', 'Papan Markah Dunia'] },
              { name: 'Basic', nameBM: 'Asas', price: 'RM19.90', period: isEN ? '/month' : '/bulan', color: '#2196F3', features: isEN ? ['Unlimited games', 'All 8 subjects', 'Easy + Medium difficulty', 'Leaderboard + Progress'] : ['Permainan tanpa had', 'Semua 8 subjek', 'Mudah + Sederhana', 'Papan markah + Kemajuan'] },
              { name: 'Pro', nameBM: 'Pro', price: 'RM49.90', period: isEN ? '/month' : '/bulan', color: '#E85D3A', popular: true, features: isEN ? ['Everything in Basic', 'All difficulty levels', 'Class management', 'PDF reports', 'Priority support'] : ['Semua dalam Asas', 'Semua tahap', 'Pengurusan kelas', 'Laporan PDF', 'Sokongan keutamaan'] },
              { name: 'School', nameBM: 'Sekolah', price: 'RM199', period: isEN ? '/month' : '/bulan', color: '#009688', features: isEN ? ['Everything in Pro', '50+ students', 'School tournaments', 'Bulk reports', 'Dedicated support'] : ['Semua dalam Pro', '50+ pelajar', 'Pertandingan sekolah', 'Laporan pukal', 'Sokongan khusus'] },
            ].map((plan, i) => (
              <div key={i} className={`landing-price-card ${plan.popular ? 'landing-price-popular' : ''}`}>
                {plan.popular && <div className="landing-price-badge">{isEN ? 'Most Popular' : 'Paling Popular'}</div>}
                <h3 style={{ color: plan.color }}>{isEN ? plan.name : plan.nameBM}</h3>
                <div className="landing-price-amount">{plan.price}<span>{plan.period || ''}</span></div>
                <ul>{plan.features.map((f, j) => <li key={j}><Check size={16} style={{ color: '#22c55e' }} /> {f}</li>)}</ul>
                <button className="landing-price-btn" style={{ background: plan.color }} onClick={() => navigate('/register')}>{isEN ? 'Get Started' : 'Mula Sekarang'}</button>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* AFFILIATE HARDSELL */}
      <section className="landing-affiliate" id="affiliate-section">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <div className="landing-affiliate-card">
            <div className="landing-affiliate-left">
              <div className="landing-affiliate-badge">{isEN ? 'AFFILIATE PROGRAM' : 'PROGRAM AFFILIATE'}</div>
              <h2>{isEN ? 'Teachers, Earn RM5,000+/month by Sharing PANTASS' : 'Cikgu, Jana RM5,000+/bulan dengan Kongsi PANTASS'}</h2>
              <p>{isEN
                ? 'Join our affiliate program and earn 30% recurring commission for every parent who subscribes through your link. Share once, earn every month!'
                : 'Sertai program affiliate kami dan dapatkan 30% komisen berulang untuk setiap ibu bapa yang melanggan melalui link anda. Kongsi sekali, jana setiap bulan!'}</p>
              <div className="landing-affiliate-math">
                <div className="landing-affiliate-calc">
                  <span>{isEN ? '10 parents × RM19.90 × 30%' : '10 ibu bapa × RM19.90 × 30%'}</span>
                  <strong>= RM59.70/{isEN ? 'month' : 'bulan'}</strong>
                </div>
                <div className="landing-affiliate-calc">
                  <span>{isEN ? '50 parents × RM19.90 × 30%' : '50 ibu bapa × RM19.90 × 30%'}</span>
                  <strong>= RM298.50/{isEN ? 'month' : 'bulan'}</strong>
                </div>
                <div className="landing-affiliate-calc landing-affiliate-calc-big">
                  <span>{isEN ? '200 parents × RM19.90 × 30%' : '200 ibu bapa × RM19.90 × 30%'}</span>
                  <strong>= RM1,194/{isEN ? 'month' : 'bulan'}</strong>
                </div>
              </div>
              <button className="landing-cta-primary" style={{ background: '#009688', marginTop: '20px' }} onClick={() => navigate('/register')}>
                {isEN ? 'Join Affiliate Program — Free' : 'Sertai Program Affiliate — Percuma'}
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* SEO: Kuiz Popular — Internal Links for Google */}
      <section className="landing-quiz-links" style={{ background: '#0F172A', padding: '60px 0', borderTop: '1px solid #1e293b' }}>
        <motion.div className="landing-section-inner" {...fadeUp}>
          <h2 className="landing-section-title" style={{ marginBottom: 8 }}>
            {isEN ? 'Popular Quizzes' : 'Kuiz Popular'}
          </h2>
          <p style={{ textAlign: 'center', color: '#64748b', marginBottom: 32, fontSize: 15 }}>
            {isEN ? 'Interactive quizzes aligned with KSSR & KSSM curriculum. Free to try.' : 'Kuiz interaktif selaras kurikulum KSSR & KSSM. Percuma untuk cuba.'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, maxWidth: 900, margin: '0 auto' }}>
            {[
              { href: '/kuiz/matematik/tahun-1', label: 'Latihan Matematik Tahun 1', icon: Calculator, color: '#E85D3A' },
              { href: '/kuiz/matematik/tahun-2', label: 'Latihan Matematik Tahun 2', icon: Calculator, color: '#E85D3A' },
              { href: '/kuiz/matematik/tahun-3', label: 'Latihan Matematik Tahun 3', icon: Calculator, color: '#E85D3A' },
              { href: '/kuiz/matematik/tahun-4', label: 'Latihan Matematik Tahun 4', icon: Calculator, color: '#E85D3A' },
              { href: '/kuiz/matematik/tahun-5', label: 'Latihan Matematik Tahun 5', icon: Calculator, color: '#E85D3A' },
              { href: '/kuiz/matematik/tahun-6', label: 'Latihan Matematik Tahun 6', icon: Calculator, color: '#E85D3A' },
              { href: '/kuiz/sains/tahun-4', label: 'Kuiz Sains Tahun 4', icon: FlaskConical, color: '#009688' },
              { href: '/kuiz/sains/tahun-5', label: 'Kuiz Sains Tahun 5', icon: FlaskConical, color: '#009688' },
              { href: '/kuiz/sains/tahun-6', label: 'Kuiz Sains Tahun 6', icon: FlaskConical, color: '#009688' },
              { href: '/kuiz/bahasa-melayu/tahun-3', label: 'Latihan BM Tahun 3', icon: BookOpen, color: '#2196F3' },
              { href: '/kuiz/english/tahun-4', label: 'English Quiz Year 4', icon: Globe, color: '#E85D3A' },
              { href: '/kuiz/sejarah/tingkatan-1', label: 'Kuiz Sejarah Tingkatan 1', icon: Clock, color: '#3b82f6' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <a key={i} href={item.href} data-testid={`quiz-link-${i}`}
                  style={{
                    background: '#1e293b', borderRadius: 12, padding: '14px 16px',
                    display: 'flex', alignItems: 'center', gap: 12,
                    textDecoration: 'none', color: '#f8fafc',
                    border: '1px solid #334155',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    fontSize: 14, fontWeight: 600,
                  }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={18} color="white" />
                  </div>
                  {item.label}
                </a>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <a href="/register" style={{ color: '#2196F3', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
              {isEN ? 'View all subjects & levels →' : 'Lihat semua subjek & tahap →'}
            </a>
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="landing-final-cta">
        <motion.div className="landing-section-inner" {...fadeUp}>
          <h2>
            {isEN
              ? 'Your Child Deserves to Love Learning.'
              : 'Anak Anda Layak untuk Suka Belajar.'}
          </h2>
          <p>
            {isEN
              ? 'Join thousands of Malaysian families who transformed homework time into game time. Start free today — no credit card required.'
              : 'Sertai ribuan keluarga Malaysia yang ubah masa kerja rumah jadi masa bermain. Mula percuma hari ini — tiada kad kredit diperlukan.'}
          </p>
          <a href="/register" className="landing-cta-primary landing-cta-big" data-testid="final-cta-register">
            {isEN ? 'Create Free Account Now' : 'Buat Akaun Percuma Sekarang'}
            <ArrowRight size={22} />
          </a>
          <div className="landing-guarantee">
            <Shield size={16} />
            <span>{isEN ? '7-day money-back guarantee on all paid plans' : 'Jaminan wang dikembalikan 7 hari untuk semua pelan berbayar'}</span>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <img src="/pantass-logo.jpg" alt="PANTASS" style={{ width: 28, height: 28, borderRadius: 6 }} />
            <span>PANTASS</span>
          </div>
          <div className="landing-footer-links">
            <a href="/pricing">{isEN ? 'Pricing' : 'Harga'}</a>
            <a href="/blog/tuisyen-vs-app-belajar">Blog</a>
            <a href="/leaderboard">{isEN ? 'Leaderboard' : 'Papan Markah'}</a>
            <a href="/guide">{isEN ? 'Guide' : 'Panduan'}</a>
            <a href="/privacy">{isEN ? 'Privacy Policy' : 'Dasar Privasi'}</a>
            <a href="/terms">{isEN ? 'Terms of Service' : 'Terma Perkhidmatan'}</a>
            <a href="mailto:support@pantass.com">support@pantass.com</a>
          </div>
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: '6px 16px', justifyContent: 'center' }}>
            <a href="/kuiz/matematik/tahun-1" style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'none' }}>Matematik Tahun 1</a>
            <a href="/kuiz/matematik/tahun-4" style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'none' }}>Matematik Tahun 4</a>
            <a href="/kuiz/matematik/tahun-6" style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'none' }}>Matematik Tahun 6</a>
            <a href="/kuiz/sains/tahun-5" style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'none' }}>Sains Tahun 5</a>
            <a href="/kuiz/bahasa-melayu/tahun-3" style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'none' }}>BM Tahun 3</a>
            <a href="/kuiz/english/tahun-4" style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'none' }}>English Year 4</a>
          </div>
          <p className="landing-footer-copy">&copy; 2026 PANTASS. {isEN ? 'All rights reserved.' : 'Hak cipta terpelihara.'}</p>
          <p style={{ marginTop: 8, fontSize: 12, color: '#94a3b8', letterSpacing: '0.05em' }}>POWERED BY UNDERADS</p>
        </div>
      </footer>
    </div>
  );
}
